import {
  Card, ErrorState, Grid, LoadingState, Notice, PageHeader, Stack, Stat,
  calendarDays, useApi, useI18n,
  type DashboardMetric,
} from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

/** The metrics that are money, and therefore format as money rather than as a count. */
const MONEY_METRICS = new Set([
  'finance.invoiced',
  'finance.collected',
  'finance.outstanding',
]);

/**
 * The nursery dashboard (FR-ADM-01, FR-ADM-02).
 *
 * Every figure comes from one endpoint that computes them all against the same rules the underlying lists
 * use — the absence rule for presence, the issued-invoice rule for what is owed. A dashboard whose numbers
 * cannot be reconciled with the screens behind them stops being used within a week.
 *
 * Each tile links through to the list it counts (FR-ADM-03), so a figure that looks wrong is one click
 * from the rows that made it.
 */
export function Dashboard() {
  const api = useApi();
  const { t, formatNumber, formatMoney, formatDate, has, settings } = useI18n();

  const dashboard = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => api.admin.dashboard(),
  });

  if (dashboard.isPending) return <LoadingState />;
  if (dashboard.isError) {
    return <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />;
  }

  const data = dashboard.data;

  const render = (metric: DashboardMetric) => {
    const label = has(`metric.${metric.key}`) ? t(`metric.${metric.key}`) : metric.key;

    const value = MONEY_METRICS.has(metric.key)
      ? formatMoney(metric.value, data.currency)
      : formatNumber(metric.value);

    const tile = <Stat label={label} value={value} />;

    // A metric with nowhere to go is still shown; it simply is not a link.
    return metric.link
      ? <Link key={metric.key} className="dg-stat-link" to={metric.link}>{tile}</Link>
      : <div key={metric.key}>{tile}</div>;
  };

  return (
    <Stack>
      <PageHeader title={t('dashboard.title')} lede={formatDate(data.date)} />

      {/* A day of zeroes has an explanation, and saying it beats leaving an owner to wonder. */}
      {!data.operatingDay && <Notice tone="info">{t('attendance.closedDay')}</Notice>}

      <Grid>{data.metrics.map(render)}</Grid>

      {/* Absent rather than zeroed for a caller without financial access: "income: 0" reads as a fact
          about the nursery instead of a fact about the reader's permissions (FR-STF-11). */}
      {data.upcoming.length > 0 && (
        <Card title={t('calendar.title')}>
          <Stack tight>
            {data.upcoming.map((event) => (
              <div key={event.id} className="dg-row dg-row--between">
                <span>
                  <strong>{event.title}</strong>
                  <span className="dg-muted"> · {t(`calendar.type.${event.eventType}`)}</span>
                </span>
                {/* The first day as the calendar counts it: an all-day entry's instant names a UTC date. */}
                <span className="dg-muted">{formatDate(calendarDays(event, settings).from)}</span>
              </div>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
